import { useLogger } from 'common';
import { useState, useEffect, useCallback } from 'react';

import { statusCache, StatusCacheData } from '../../utils/statusCache';

type StatusHook<T> = () => T;
type CacheKey = keyof StatusCacheData;

export function useCachedStatus<T extends StatusCacheData[CacheKey]>(
  cacheKey: CacheKey,
  statusHook: StatusHook<T>,
  expirationMs: number = 5 * 60 * 1000,
) {
  const logger = useLogger(`useCachedStatus:${cacheKey}`);
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCacheHit, setIsCacheHit] = useState(false);

  const freshData = statusHook();

  useEffect(() => {
    const cachedData = statusCache.get(cacheKey) as T | null;

    if (cachedData) {
      logger.debug('Using cached data for initial load');
      setData(cachedData);
      setIsCacheHit(true);
      setIsLoading(false);
    } else {
      logger.debug('No valid cache found, waiting for fresh data');
      setIsCacheHit(false);
    }
  }, [cacheKey, expirationMs]);

  useEffect(() => {
    if (freshData) {
      const isDataChanged = !data || JSON.stringify(data) !== JSON.stringify(freshData);

      if (isDataChanged) {
        logger.debug('Fresh data received, updating cache and state');
        setData(freshData);
        statusCache.set(cacheKey, freshData as StatusCacheData[typeof cacheKey], expirationMs);
        setIsLoading(false);

        if (isCacheHit) {
          logger.debug('Transitioned from cached to fresh data');
          setIsCacheHit(false);
        }
      } else if (!isCacheHit) {
        setIsLoading(false);
      }
    }
  }, [freshData, data, cacheKey, expirationMs, isCacheHit]);

  const refreshCache = useCallback(() => {
    logger.debug('Manually refreshing cache');
    statusCache.remove(cacheKey);
    setIsCacheHit(false);
    setIsLoading(true);
  }, [cacheKey]);

  const clearCache = useCallback(() => {
    logger.debug('Clearing cache entry');
    statusCache.remove(cacheKey);
    setIsCacheHit(false);
  }, [cacheKey]);

  return {
    data: data || freshData,
    isLoading: isLoading && !data,
    isCacheHit,
    refreshCache,
    clearCache,
  };
}
